import { colors, colorSystem } from '@/styles/theme'
import { HStack, Icon } from '@musinsa/mwms-ds'
import styled, { css } from 'styled-components'
import Text from '@/components/content/Text'
import useModal from '@/hooks/useModal'
import { Capacity } from '@/services/capacity/types'
import { SetCapacityModal } from './SetCapacityModal'
import { Suspense } from 'react'
import { BaseCell } from '../shared/BaseCell'
import { CELL_WIDTH } from '../../constants'
import { tokens } from '@musinsa/layer-tokens/desktop'
import { CapaCell } from './CapaCell'
import { useCutOffDate } from '../../hooks/useCutOffDate'
import { DATE_FORMAT } from '@/utils/consts'
import dayjs from 'dayjs'
import { useHoveredWarehouse } from '../../hooks/useHoveredWarehouse'

interface HeaderProps {
  warehouseCode: string
  warehouseName: string
  isOperation: boolean
  isUnlimited?: boolean
}

/** 센터명 헤더 셀 - 상단 sticky 영역 */
export function WarehouseHeaderCell({ warehouseCode, warehouseName, isOperation, isUnlimited }: HeaderProps) {
  const { cutOffDate } = useCutOffDate()
  const isCutOffDateToday = dayjs().format(DATE_FORMAT.date) === cutOffDate

  const setCapacityModal = useModal()
  const [hoveredWarehouse, setHoveredWarehouse] = useHoveredWarehouse()
  const isColumnHover = hoveredWarehouse === warehouseCode

  return (
    <>
      <WarehouseHeaderWrap
        onMouseEnter={() => setHoveredWarehouse(warehouseCode)}
        onMouseLeave={() => setHoveredWarehouse(null)}
        style={{ backgroundColor: isColumnHover ? 'rgba(36, 94, 255, 0.04)' : undefined }}
      >
        <WarehouseCell
          $clickable={isCutOffDateToday && isOperation}
          onClick={() => {
            if (!isOperation) return
            setCapacityModal.onOpenModal()
          }}
        >
          <HStack gap={4} align="center">
            <Circle $color={isOperation ? tokens.layerDesktop['fg-accent'] : colors.gray500} />
            <Text kind="Body_14_medium" color={isOperation ? colorSystem.text.table.cell : colorSystem.text.common.disabled}>
              {warehouseName}
            </Text>
          </HStack>
          {isUnlimited && (
            <Tag className="tag">
              <Text kind="Body_12_regular">무한</Text>
            </Tag>
          )}
          <Icon className="setting-icon" name="setting" size={16} color="gray500" />
        </WarehouseCell>
      </WarehouseHeaderWrap>

      {setCapacityModal.open && (
        <Suspense fallback={null}>
          <SetCapacityModal warehouseCode={warehouseCode} onClose={setCapacityModal.onCloseModal} />
        </Suspense>
      )}
    </>
  )
}

interface Props {
  warehouseCode: string
  warehouseName: string
  capacities: Capacity[]
}

/** 케파 데이터 영역 (B2C 케파) */
export function CapaItem({ warehouseCode, warehouseName, capacities }: Props) {
  const [hoveredWarehouse, setHoveredWarehouse] = useHoveredWarehouse()
  const isColumnHover = hoveredWarehouse === warehouseCode

  const b2cCapacity = capacities.find((c) => c.capacityType === 'B2C')

  return (
    <CapaItemWrap
      onMouseEnter={() => setHoveredWarehouse(warehouseCode)}
      onMouseLeave={() => setHoveredWarehouse(null)}
      style={{ backgroundColor: isColumnHover ? 'rgba(36, 94, 255, 0.04)' : undefined }}
    >
      {b2cCapacity ? <CapaCell data={b2cCapacity} warehouseCode={warehouseCode} warehouseName={warehouseName} /> : <NotOperationCell />}
    </CapaItemWrap>
  )
}

const WarehouseHeaderWrap = styled.div`
  min-width: ${CELL_WIDTH}px;
  display: flex;
  flex-direction: column;
`

const CapaItemWrap = styled.div`
  min-width: ${CELL_WIDTH}px;
  height: 36px;
  display: flex;
  flex-direction: column;
`

function NotOperationCell() {
  return (
    <BaseCell style={{ flex: 1, justifyContent: 'center', alignContent: 'center', background: colors.gray050, borderBottom: 'none' }}>
      <Text kind="Body_12_regular" color={colorSystem.text.common.tertiary}>
        미운영
      </Text>
    </BaseCell>
  )
}

const WarehouseCell = styled(BaseCell)<{ $clickable: boolean }>`
  height: 44px;
  border-bottom: 1px solid ${colors.gray400};

  justify-content: space-between;

  .setting-icon {
    display: none;
  }

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;

      &:hover {
        .setting-icon {
          display: block;
        }
        .tag {
          display: none;
        }
      }
    `}
`

const Tag = styled.div`
  border-radius: 4px;
  border: 1px solid ${colorSystem.border.element};
  padding: 0px 4px;
`

const Circle = styled.div<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 99%;
  background-color: ${({ $color }) => $color};
`
